import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Trophy,
  X,
} from "lucide-react";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  formatPortalTime,
} from "./useStudentPortalFetch";

const OPTION_LETTERS = ["A", "B", "C", "D"];

function marksLabel(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "1 mark";
  return n === 1 ? "1 mark" : `${n} marks`;
}

function gradeFromPercentage(percentage) {
  const pct = Number(percentage);
  if (Number.isNaN(pct)) return null;
  if (pct >= 90) return "A+";
  if (pct >= 80) return "A";
  if (pct >= 70) return "B+";
  if (pct >= 60) return "B";
  if (pct >= 50) return "C";
  if (pct >= 40) return "D";
  return "F";
}

function remainingLabel(endsAt) {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  const ms = end - Date.now();
  if (ms <= 0) return "Time up";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDateTime(iso, dateValue, timeValue) {
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const date = formatPortalDate(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`
      );
      const time = formatPortalTime(
        `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`
      );
      return `${date}, ${time}`;
    }
  }
  const date = formatPortalDate(dateValue);
  const time = formatPortalTime(timeValue);
  if (date && time) return `${date}, ${time}`;
  return date || time || "";
}

function earlierIso(a, b) {
  const ta = a ? new Date(a).getTime() : NaN;
  const tb = b ? new Date(b).getTime() : NaN;
  if (Number.isNaN(ta)) return b || null;
  if (Number.isNaN(tb)) return a || null;
  return ta <= tb ? a : b;
}

function TestTimingPanel({ meta, test, attempt }) {
  const opens = formatDateTime(
    meta?.window_starts_at,
    meta?.assessment_date,
    meta?.start_time
  );
  const closes = formatDateTime(
    meta?.window_ends_at,
    meta?.due_date || meta?.assessment_date,
    meta?.due_time || meta?.end_time
  );
  const durationMins = Number(test?.test_duration_minutes || 0);
  const durationEndIso =
    durationMins > 0
      ? new Date(Date.now() + durationMins * 60 * 1000).toISOString()
      : null;
  const finishByIso = attempt?.ends_at
    ? attempt.ends_at
    : earlierIso(durationEndIso, meta?.window_ends_at);
  const finishBy = formatDateTime(finishByIso);

  if (!opens && !closes && !durationMins && !finishBy) return null;

  return (
    <ul className="ems-test-timing">
      {opens ? (
        <li>
          <span>Opens</span>
          <b>{opens}</b>
        </li>
      ) : null}
      {closes ? (
        <li>
          <span>Closes</span>
          <b>{closes}</b>
        </li>
      ) : null}
      {durationMins ? (
        <li>
          <span>Time allowed</span>
          <b>{durationMins} minutes</b>
        </li>
      ) : null}
      {finishBy ? (
        <li>
          <span>{attempt ? "This attempt ends" : "Must finish by"}</span>
          <b>{finishBy}</b>
        </li>
      ) : null}
    </ul>
  );
}

function QuizOptions({
  question,
  revealCorrect,
  revealSelectionFeedback,
  locked,
  busy,
  onPick,
}) {
  return (
    <div className="ems-quiz-options">
      {(question?.options || []).map((option, optionIndex) => {
        const selected = question.selected === option.key;
        const isCorrectOption =
          revealCorrect &&
          question.correct_answer &&
          question.correct_answer === option.key;
        const isWrongSelected =
          revealSelectionFeedback && selected && question.is_correct === false;
        const isCorrectSelected =
          revealSelectionFeedback && selected && question.is_correct === true;

        return (
          <button
            key={option.key}
            type="button"
            disabled={busy || locked}
            className={[
              "ems-quiz-option",
              selected ? "is-selected" : "",
              isCorrectOption || isCorrectSelected ? "is-correct" : "",
              isWrongSelected ? "is-wrong" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => {
              if (!locked && !busy && onPick) onPick(question, option.key);
            }}
          >
            <span className="ems-quiz-option-letter">
              {option.label || OPTION_LETTERS[optionIndex] || "?"}
            </span>
            <span className="ems-quiz-option-text">{option.text}</span>
            {isCorrectOption || isCorrectSelected ? <Check size={18} /> : null}
            {isWrongSelected ? <X size={18} /> : null}
          </button>
        );
      })}
    </div>
  );
}

function ScoreBanner({ result, submitted }) {
  if (!submitted) return null;
  const showScore =
    result &&
    (result.marks_obtained != null ||
      result.percentage != null ||
      (result.result_status && result.result_status !== "Submitted"));
  const grade =
    result?.grade || gradeFromPercentage(result?.percentage);

  return (
    <section className="ems-quiz-results">
      <div className="ems-quiz-score-ring" aria-hidden="true">
        <Trophy size={28} strokeWidth={2} />
      </div>
      {showScore ? (
        <>
          <h2>{result.result_status || "Submitted"}</h2>
          <p>
            You scored {result.marks_obtained ?? "—"}
            {result.total_marks != null ? ` / ${result.total_marks}` : ""} marks
            {result.percentage != null ? ` · ${result.percentage}%` : ""}
            {grade ? ` · Grade ${grade}` : ""}.
          </p>
        </>
      ) : (
        <>
          <h2>Submitted</h2>
          <p>Your attempt has been submitted.</p>
        </>
      )}
    </section>
  );
}

function withHttp(url) {
  const text = String(url ?? "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text)) return text;
  return `https://${text}`;
}

function StudentAssessmentNotes({
  row,
  className = "",
  hideInstructions = false,
  hideRemarks = false,
}) {
  if (!row) return null;

  const noteOk = (value) => {
    const text = String(value ?? "").trim();
    return text !== "" && text.toLowerCase() !== "null" && text !== "-";
  };

  const instructions =
    !hideInstructions && noteOk(row.instructions)
      ? String(row.instructions).trim()
      : "";
  const referenceUrl = noteOk(row.reference_url)
    ? String(row.reference_url).trim()
    : "";
  const remarks =
    !hideRemarks && noteOk(row.remarks) ? String(row.remarks).trim() : "";

  if (!instructions && !referenceUrl && !remarks) return null;

  return (
    <div className={`ems-student-assessment-notes ${className}`.trim()}>
      {instructions ? (
        <p className="ems-student-meta">
          <strong>Instructions:</strong> {instructions}
        </p>
      ) : null}
      {referenceUrl ? (
        <p className="ems-student-meta">
          <strong>Reference:</strong>{" "}
          <a
            href={withHttp(referenceUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="ems-student-link"
          >
            {referenceUrl}
          </a>
        </p>
      ) : null}
      {remarks ? (
        <p className="ems-student-meta">
          <strong>Remarks:</strong> {remarks}
        </p>
      ) : null}
    </div>
  );
}

export default function StudentTestPage() {
  const { scheduleStudentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);
  const [test, setTest] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [savingId, setSavingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [instructionsAccepted, setInstructionsAccepted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/ems/portal/assessments/${scheduleStudentId}/test`,
        { headers: getApiHeaders() }
      );
      const payload = res?.data?.data || {};
      setMeta(payload.schedule_student || null);
      setTest(payload.test || null);
      setAttempt(payload.attempt || null);
      setQuestions(Array.isArray(payload.questions) ? payload.questions : []);
      const attemptStatus =
        payload.attempt?.attempt_status ||
        payload.schedule_student?.attempt_status ||
        "";
      const isSubmitted = Boolean(
        payload.submitted ||
          ["Submitted", "Auto Submitted"].includes(attemptStatus)
      );
      setSubmitted(isSubmitted);
      if (isSubmitted && (payload.marks_obtained != null || payload.result_status)) {
        setResult(payload);
      } else {
        setResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load this test."
      );
    } finally {
      setLoading(false);
    }
  }, [scheduleStudentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!attempt?.ends_at || submitted) return undefined;
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [attempt?.ends_at, submitted]);

  const current = questions[index] || null;
  const answeredCount = questions.filter((q) => q.selected).length;
  const progressPct = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;
  const timeLeft = remainingLabel(attempt?.ends_at);
  const timedOut = Boolean(
    attempt?.ends_at && new Date(attempt.ends_at).getTime() <= nowTick
  );
  const instructionsText = String(meta?.instructions ?? "").trim();
  const remarksText = String(meta?.remarks ?? "").trim();
  const validNote = (text) =>
    text !== "" && text.toLowerCase() !== "null" && text !== "-";
  const hasInstructions =
    validNote(instructionsText) || validNote(remarksText);
  const canClickStart =
    meta?.can_start_test !== false &&
    (!hasInstructions || instructionsAccepted);

  const applyAttemptPayload = (payload) => {
    if (payload?.attempt) setAttempt(payload.attempt);
    else if (payload?.organization_ems_student_test_attempt_id) {
      setAttempt((prev) => ({ ...(prev || {}), ...payload }));
    }
    if (Array.isArray(payload?.questions)) setQuestions(payload.questions);
    if (payload?.test) setTest(payload.test);
    const attemptStatus =
      payload?.attempt?.attempt_status || payload?.attempt_status || "";
    const isSubmitted = Boolean(
      payload?.submitted ||
        ["Submitted", "Auto Submitted"].includes(attemptStatus)
    );
    if (isSubmitted) setSubmitted(true);
    const showScore = Boolean(
      payload?.show_result_immediately ??
        payload?.test?.show_result_immediately ??
        test?.show_result_immediately
    );
    if (
      isSubmitted &&
      showScore &&
      (payload?.marks_obtained != null || payload?.result_status)
    ) {
      setResult(payload);
    }
  };

  const startTest = async () => {
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/assessments/${scheduleStudentId}/start`,
        {},
        { headers: getApiHeaders() }
      );
      applyAttemptPayload(res?.data?.data || {});
      setIndex(0);
      toast.success(res?.data?.message || "Test started.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not start the test."
      );
    }
  };

  const saveAnswer = async (question, optionKey) => {
    if (
      submitted ||
      !attempt?.organization_ems_student_test_attempt_id ||
      savingId
    ) {
      return;
    }
    if (test?.show_correct_answers && question.correct_answer) return;
    setSavingId(question.organization_ems_test_question_id);
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/attempts/${attempt.organization_ems_student_test_attempt_id}/answer`,
        {
          organization_ems_test_question_id:
            question.organization_ems_test_question_id,
          selected: optionKey,
        },
        { headers: getApiHeaders() }
      );
      const payload = res?.data?.data || {};
      setQuestions((prev) =>
        prev.map((row) =>
          String(row.organization_ems_test_question_id) ===
          String(question.organization_ems_test_question_id)
            ? {
                ...row,
                selected: payload.selected || optionKey,
                is_correct: payload.is_correct,
                correct_answer: payload.correct_answer,
                marks_awarded: payload.marks_awarded,
                answer_status: "Answered",
              }
            : row
        )
      );
    } catch (err) {
      if (err?.response?.data?.data?.result_status) {
        applyAttemptPayload(err.response.data.data);
      }
      toast.error(
        err?.response?.data?.message || err?.message || "Could not save that answer."
      );
    } finally {
      setSavingId(null);
    }
  };

  const submitTest = async () => {
    if (!attempt?.organization_ems_student_test_attempt_id || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/attempts/${attempt.organization_ems_student_test_attempt_id}/submit`,
        {},
        { headers: getApiHeaders() }
      );
      applyAttemptPayload(res?.data?.data || {});
      toast.success(res?.data?.message || "Test submitted.");
      navigate("/student/assignments");
    } catch (err) {
      const data = err?.response?.data?.data;
      if (data) applyAttemptPayload(data);
      const timedOutSubmit = Boolean(
        data?.submitted ||
          ["Submitted", "Auto Submitted"].includes(
            data?.attempt?.attempt_status || data?.attempt_status || ""
          )
      );
      if (timedOutSubmit) {
        toast.success(
          err?.response?.data?.message || "Test submitted."
        );
        navigate("/student/assignments");
      } else {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Could not submit the test."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (timedOut && attempt && !submitted) {
      submitTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut]);

  const showScore = Boolean(test?.show_result_immediately);
  const showCorrectAnswers = Boolean(test?.show_correct_answers);
  const showSelectionFeedback = showScore || showCorrectAnswers;
  const revealCorrectCurrent = Boolean(
    showCorrectAnswers && current?.correct_answer
  );
  const revealSelectionCurrent = Boolean(
    showSelectionFeedback && current?.is_correct != null
  );
  const lockCurrent = Boolean(submitted || revealCorrectCurrent);

  return (
    <StudentPortalShell
      className="ems-quiz-page"
      title={
        submitted
          ? meta?.assessment_name || "Test result"
          : meta?.assessment_name || "Online test"
      }
      subtitle={
        submitted
          ? "Your attempt has been submitted."
          : meta?.training_program_name
            ? `${meta.training_program_name}${
                timeLeft ? ` · ${timeLeft} remaining` : ""
              }`
            : "Answer the questions and submit before time runs out."
      }
      headerRight={
        <button
          type="button"
          className="ems-student-btn ems-student-btn-secondary"
          onClick={() => navigate("/student/assignments")}
        >
          Back to Assessments
        </button>
      }
    >
      <StudentPortalState loading={loading} error={error}>
        {submitted ? (
          <section className="ems-quiz-board">
            <ScoreBanner result={result} submitted={submitted && showScore} />
            {questions.map((question) => (
                  <article
                    key={question.organization_ems_test_question_id}
                    className="ems-quiz-card"
                  >
                    <div className="ems-quiz-card-meta">
                      <span className="ems-quiz-marks-chip">
                        {marksLabel(question.marks)}
                      </span>
                    </div>
                    <h2 className="ems-quiz-question">{question.question}</h2>
                    <QuizOptions
                      question={question}
                      revealCorrect={Boolean(
                        showCorrectAnswers && question.correct_answer
                      )}
                      revealSelectionFeedback={Boolean(
                        showSelectionFeedback && question.is_correct != null
                      )}
                      locked
                    />
                    {showCorrectAnswers && question.correct_answer ? (
                      <p
                        className={`ems-quiz-feedback ${
                          question.is_correct ? "is-correct" : "is-wrong"
                        }`}
                      >
                        {question.is_correct
                          ? "Correct"
                          : question.selected
                            ? "Incorrect — the right answer is marked in green."
                            : "Not answered — the right answer is marked in green."}
                      </p>
                    ) : showSelectionFeedback && question.is_correct === true ? (
                      <p className="ems-quiz-feedback is-correct">Correct</p>
                    ) : showSelectionFeedback && question.selected ? (
                      <p className="ems-quiz-feedback is-wrong">Incorrect</p>
                    ) : showSelectionFeedback && !question.selected ? (
                      <p className="ems-quiz-feedback">Not answered</p>
                    ) : null}
                  </article>
                ))}
          </section>
        ) : !attempt ? (
          <section className="ems-student-section">
            <p className="ems-student-meta">
              {test?.total_questions
                ? `${test.questions_to_attempt || test.total_questions} questions`
                : "Online test"}
              {test?.test_duration_minutes
                ? ` · ${test.test_duration_minutes} minutes`
                : ""}
              {test?.max_attempts ? ` · ${test.max_attempts} attempt(s)` : ""}
            </p>
            <TestTimingPanel meta={meta} test={test} attempt={null} />
            {hasInstructions ? (
              <div className="ems-student-instructions-gate">
                <div className="ems-student-instructions-panel">
                  <h3 className="ems-student-instructions-title">Instructions</h3>
                  {validNote(instructionsText) ? (
                    <p className="ems-student-instructions-body">
                      {instructionsText}
                    </p>
                  ) : null}
                  {validNote(remarksText) ? (
                    <p className="ems-student-instructions-body">
                      {validNote(instructionsText) ? (
                        <strong>Remarks: </strong>
                      ) : null}
                      {remarksText}
                    </p>
                  ) : null}
                </div>
                <StudentAssessmentNotes
                  row={meta}
                  hideInstructions
                  hideRemarks
                />
                <label className="ems-student-instructions-ack">
                  <input
                    type="checkbox"
                    checked={instructionsAccepted}
                    onChange={(e) => setInstructionsAccepted(e.target.checked)}
                  />
                  <span>I have read and understood the instructions</span>
                </label>
              </div>
            ) : (
              <StudentAssessmentNotes row={meta} />
            )}
            <button
              type="button"
              className="ems-student-btn ems-student-btn-primary"
              onClick={startTest}
              disabled={!canClickStart}
            >
              Start test
            </button>
            {hasInstructions && !instructionsAccepted ? (
              <p className="ems-student-meta">
                Tick the box above to enable Start test.
              </p>
            ) : null}
          </section>
        ) : (
          <section className="ems-quiz-board">
            <StudentAssessmentNotes row={meta} />
            <div className="ems-quiz-progress">
              <div className="ems-quiz-progress-copy">
                <strong>
                  Question {index + 1} of {questions.length}
                </strong>
                <span>
                  {answeredCount} answered
                  {timeLeft ? (
                    <>
                      {" "}
                      · <Clock size={14} /> {timeLeft} left
                    </>
                  ) : null}
                </span>
              </div>
              <div className="ems-quiz-progress-bar" aria-hidden="true">
                <span style={{ width: `${progressPct}%` }} />
              </div>
            </div>
            <TestTimingPanel meta={meta} test={test} attempt={attempt} />

            {current ? (
              <article className="ems-quiz-card">
                <div className="ems-quiz-card-meta">
                  <span className="ems-quiz-marks-chip">
                    {marksLabel(current.marks)}
                  </span>
                </div>
                <h2 className="ems-quiz-question">{current.question}</h2>
                <QuizOptions
                  question={current}
                  revealCorrect={revealCorrectCurrent}
                  revealSelectionFeedback={revealSelectionCurrent}
                  locked={lockCurrent || submitting}
                  busy={savingId === current.organization_ems_test_question_id}
                  onPick={saveAnswer}
                />
                {revealCorrectCurrent ? (
                  <p
                    className={`ems-quiz-feedback ${
                      current.is_correct ? "is-correct" : "is-wrong"
                    }`}
                  >
                    {current.is_correct
                      ? "Correct"
                      : "Incorrect — the right answer is marked in green."}
                  </p>
                ) : null}
              </article>
            ) : null}

            <div className="ems-quiz-nav">
              <button
                type="button"
                className="ems-student-btn ems-student-btn-secondary"
                disabled={index === 0}
                onClick={() => setIndex((value) => Math.max(0, value - 1))}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              {index < questions.length - 1 ? (
                <button
                  type="button"
                  className="ems-student-btn ems-student-btn-primary"
                  onClick={() => setIndex((value) => value + 1)}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  className="ems-student-btn ems-student-btn-primary"
                  disabled={submitting}
                  onClick={submitTest}
                >
                  {submitting ? "Submitting…" : "Submit test"}
                </button>
              )}
            </div>
          </section>
        )}
      </StudentPortalState>
    </StudentPortalShell>
  );
}
